
import { db, auth } from '../../lib/firebase';
import { Service } from '../../types';

// Helper to remove undefined values and enforce types
const cleanServicePayload = (data: Partial<Service>, isUpdate: boolean = false): any => {
    // 1. Deep copy and remove 'undefined' fields via JSON serialization
    // This resolves "FirebaseError: Unsupported field value: undefined"
    let clean = JSON.parse(JSON.stringify(data));

    // 2. Sanitize Numbers (Prevent null/NaN from breaking UI or DB)
    if (clean.price !== undefined) clean.price = Number(clean.price);
    if (isNaN(clean.price)) clean.price = 0;

    if (clean.durationMinutes !== undefined) clean.durationMinutes = Number(clean.durationMinutes);
    if (isNaN(clean.durationMinutes)) clean.durationMinutes = 0;

    if (clean.bufferMinutes !== undefined) clean.bufferMinutes = Number(clean.bufferMinutes);
    if (isNaN(clean.bufferMinutes)) clean.bufferMinutes = 0;

    if (clean.maxParticipants !== undefined) clean.maxParticipants = Number(clean.maxParticipants);
    if (isNaN(clean.maxParticipants)) clean.maxParticipants = 1;

    // 3. Type-Specific Rules (Enforce consistency)
    const type = clean.serviceType;

    if (type === 'Custom') {
        // Custom projects are negotiation-based, usually not directly bookable on a calendar
        clean.isBookable = false;
        clean.bookingFlow = 'REQUEST';
        clean.durationMinutes = 0;
        // Unlink calendar if accidentally set, as it's irrelevant
        clean.calendarId = null;
    } 
    else if (type === 'Workshop' || type === 'Event') {
        // Events are usually fixed dates or specific slots
        if (clean.isBookable === undefined && !isUpdate) clean.isBookable = true;
        if (clean.bookingFlow === undefined && !isUpdate) clean.bookingFlow = 'INSTANT';
        // Events usually have higher capacity
        if ((clean.maxParticipants === undefined || clean.maxParticipants === 1) && !isUpdate) clean.maxParticipants = 10;
    } 
    else if (type === 'Recording') {
        // Recording is the standard bookable unit
        if (clean.isBookable === undefined && !isUpdate) clean.isBookable = true;
        if (clean.bookingFlow === undefined && !isUpdate) clean.bookingFlow = 'INSTANT';
        if ((clean.durationMinutes === 0 || clean.durationMinutes === undefined) && !isUpdate) clean.durationMinutes = 60;
    }

    // 4. Creation Defaults (Only apply if creating new)
    if (!isUpdate) {
        const defaults: Partial<Service> = {
            title: 'Untitled Service',
            description: '',
            price: 0,
            currency: 'USD',
            enabled: true,
            features: [],
            studioId: null as any, 
            artistId: null as any,
            quoteFormType: 'NONE',
            isBookable: false,
            durationMinutes: 0,
            maxParticipants: 1,
            locationMode: 'PHYSICAL'
        };
        // Merge defaults, allowing 'clean' data to override
        clean = { ...defaults, ...clean };
    }

    // 5. Final Safety Checks for Required Fields
    if (typeof clean.price !== 'number') clean.price = 0;
    if (typeof clean.title !== 'string') clean.title = 'Untitled';
    
    return clean;
};

export const getServices = async (): Promise<Service[]> => {
  const snap = await db.collection('services').get();
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Service));
};

export const subscribeToServices = (callback: (services: Service[]) => void) => {
    return db.collection('services').onSnapshot(snap => {
        const services = snap.docs.map(d => ({ id: d.id, ...d.data() } as Service));
        callback(services);
    }, error => {
        console.error("Service subscription failed", error);
    });
};

export const getServicesForStudio = async (studioId: string): Promise<Service[]> => {
  const snap = await db.collection('services').where('studioId', '==', studioId).get();
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Service));
};

export const createService = async (service: Service) => {
  const payload = cleanServicePayload(service, false);
  await db.collection('services').add(payload);
};

export const updateService = async (id: string, data: Partial<Service>) => {
  const payload = cleanServicePayload(data, true);
  await db.collection('services').doc(id).update(payload);
};

export const deleteService = async (id: string) => {
  await db.collection('services').doc(id).delete();
};

export const seedDefaultServices = async () => {
    const currentUser = auth.currentUser;
    const ownerId = currentUser ? currentUser.uid : 'system';

    const services = [
        { 
            title: 'Stereo Mixing', 
            serviceType: 'Custom',
            price: 250, 
            description: 'Professional Mixing for a single track.', 
            enabled: true,
            isBookable: false,
            studioId: ownerId,
            durationMinutes: 0
        },
        {
            title: 'Recording Session (1Hr)',
            serviceType: 'Recording',
            price: 100,
            description: 'In-studio recording with engineer.',
            enabled: true,
            isBookable: true,
            durationMinutes: 60,
            studioId: ownerId,
            location: 'Studio A'
        }
    ];
    
    for(const s of services) {
        // @ts-ignore
        await createService(s);
    }
};
